import { createSlice } from '@reduxjs/toolkit';

import { RootState } from '..';
import {
  adminGetUserById,
  getUserListForDropDownAsAdmin,
} from './AdminUsersApi';
import {
  deleteUser,
  getEnrolmentCourseRelatedStudents,
  getEnrolmentCourseRelatedTutors,
} from './UserApi';
import {
  removeAdminPortalEditModeValueFromLocalStorage,
  removeAuthTokenToLocalStorage,
  removeAuthUserToLocalStorage,
} from '../../helper';

export type TIdentifiedUserRole = 'STUDENT' | 'TUTOR';

export const IDENTIFIED_USER_ROLES = {
  STUDENT: 'STUDENT' as TIdentifiedUserRole,
  TUTOR: 'TUTOR' as TIdentifiedUserRole,
};

export interface IRoleNotifications {
  email: {
    classReminders: {
      types: string[];
      [key: string]: any;
    };
    homeworks: {
      types: string[];
      [key: string]: any;
    };
  };
  whatsapp: {
    classReminders: {
      types: string[];
      [key: string]: any;
    };
    homeworks: {
      types: string[];
      [key: string]: any;
    };
  };
}

export interface IUserNotifications {
  email: {
    classReminders: {
      [key: string]: boolean;
    };
    homeworks: {
      [key: string]: boolean;
    };
  };
  whatsapp: {
    classReminders: {
      [key: string]: boolean;
    };
    homeworks: {
      [key: string]: boolean;
    };
  };
}

export interface IUserRole {
  _id: string;
  name: 'Admin' | 'Approver' | 'Enterer' | 'Student' | 'Tutor' | 'Guest';
  type: 'admin' | 'user';
  createdAt: string;
  permissions: IUserPermissions;
  isGuest: boolean;
  updatedAt?: string;
  notifications: IRoleNotifications;
}
export interface IBaseUser {
  _id: string;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  avatar?: string;
  createdAt: string;
  updatedAt?: string;
  role: IUserRole;
  lastLogin?: string;
}

// Course
type TAdminCourseView = 'view';
type TAdminCourseCreate = 'create';
type TAdminCourseEdit = 'edit';
type TAdminCourseDelete = 'delete';
type TAdminCoursePublish = 'publish';
type TCourseView = 'view';
type TUserPermissionAdminCourses = Array<
  | TAdminCourseView
  | TAdminCourseCreate
  | TAdminCourseEdit
  | TAdminCourseDelete
  | TAdminCoursePublish
>;
type TUserPermissionCourses = Array<TCourseView>;
// COURSE TEMPLATE
export type TAdminCourseTemplateCreate = 'create';
export type TAdminCourseTemplateView = 'view';
type TUserPermissionAdminCourseTemplates = Array<
  TAdminCourseTemplateCreate | TAdminCourseTemplateView
>;

// Schedule
type TScheduleView = 'view';
type TScheduleEdit = 'edit';
type TAdminScheduleCreate = 'create';
type TAdminScheduleView = 'view';
type TAdminScheduleEdit = 'edit';
type TUserPermissionAdminSchedule = Array<
  TAdminScheduleCreate | TAdminScheduleView | TAdminScheduleEdit
>;
type TUserPermissionSchedule = Array<TScheduleView | TScheduleEdit>;

// Profile
type TProfileView = 'view';
type TProfileEdit = 'edit';
type TProfileDelete = 'delete';
type TProfileQualifications = 'qualifications';
type TProfileSubjects = 'subjects';
type TProfileParent = 'parentDetails';
type TProfileTutor = 'tutorDetails';
type TProfileBank = 'bankDetails';
type TProfileExamDate = 'examDate';
type TProfileCurriculum = 'curriculum';
type TProfileNationality = 'nationality';
type TProfileEmergencyNumber = 'emergencyNumber';
type TProfileDateOfBirth = 'dateOfBirth';
type TUserPermissionProfile = Array<
  | TProfileView
  | TProfileEdit
  | TProfileDelete
  | TProfileQualifications
  | TProfileSubjects
  | TProfileParent
  | TProfileTutor
  | TProfileBank
  | TProfileCurriculum
  | TProfileExamDate
  | TProfileNationality
  | TProfileEmergencyNumber
  | TProfileDateOfBirth
>;

// Lesson History
type TAdminLessonHistoryView = 'view';
type TLessonHistoryView = 'view';
type TUserPermissionAdminLessonHistory = Array<TAdminLessonHistoryView>;
type TUserPermissionLessonHistory = Array<TLessonHistoryView>;

// Trial Class
type TTrialClassCreate = 'create';
type TTrialClassView = 'view';
type TAdminTrialClassCreate = 'create';
type TAdminTrialClassView = 'view';
type TAdminTrialClassEdit = 'edit';
type TUserPermissionTrialClass = Array<TTrialClassCreate | TTrialClassView>;
type TUserPermissionAdminTrialClass = Array<
  TAdminTrialClassCreate | TAdminTrialClassView | TAdminTrialClassEdit
>;

export const USER_PERMISSION_INDIVIDUALS: {
  // COURSE
  ADMIN_COURSE_VIEW: TAdminCourseView;
  ADMIN_COURSE_CREATE: TAdminCourseCreate;
  ADMIN_COURSE_EDIT: TAdminCourseEdit;
  ADMIN_COURSE_DELETE: TAdminCourseDelete;
  ADMIN_COURSE_PUBLISH: TAdminCoursePublish;
  COURSE_VIEW: TCourseView;
  // COURSE TEMPLATE
  ADMIN_COURSE_TEMPLATE_CREATE: TAdminCourseTemplateCreate;
  ADMIN_COURSE_TEMPLATE_VIEW: TAdminCourseTemplateView;
  // SCHEDULE
  ADMIN_SCHEDULE_CREATE: TAdminScheduleCreate;
  ADMIN_SCHEDULE_VIEW: TAdminScheduleView;
  ADMIN_SCHEDULE_EDIT: TAdminScheduleEdit;
  SCHEDULE_VIEW: TScheduleView;
  SCHEDULE_EDIT: TScheduleEdit;
  // LESSON HISTORY
  ADMIN_LESSON_HISTORY_VIEW: TAdminLessonHistoryView;
  LESSON_HISTORY_VIEW: TLessonHistoryView;
  // PROFILE
  PROFILE_VIEW: TProfileView;
  PROFILE_EDIT: TProfileEdit;
  PROFILE_DELETE: TProfileDelete;
  PROFILE_QUALIFICATIONS: TProfileQualifications;
  PROFILE_SUBJECTS: TProfileSubjects;
  PROFILE_PARENT: TProfileParent;
  PROFILE_TUTOR: TProfileTutor;
  PROFILE_BANK: TProfileBank;
  PROFILE_CURRICULUM: TProfileCurriculum;
  PROFILE_EXAM_DATE: TProfileExamDate;
  PROFILE_NATIONALITY: TProfileNationality;
  PROFILE_EMERGENCY_NUMBER: TProfileEmergencyNumber;
  // TRIAL CLASS
  TRIAL_CLASS_VIEW: TAdminTrialClassView;
  TRIAL_CLASS_CREATE: TAdminTrialClassCreate;
  ADMIN_TRIAL_CLASS_VIEW: TAdminTrialClassView;
  ADMIN_TRIAL_CLASS_CREATE: TAdminTrialClassCreate;
  ADMIN_TRIAL_CLASS_EDIT: TAdminTrialClassEdit;
  PROFILE_DATE_OF_BIRTH: TProfileDateOfBirth;
} = {
  // COURSE
  ADMIN_COURSE_VIEW: 'view',
  ADMIN_COURSE_CREATE: 'create',
  ADMIN_COURSE_EDIT: 'edit',
  ADMIN_COURSE_DELETE: 'delete',
  ADMIN_COURSE_PUBLISH: 'publish',
  COURSE_VIEW: 'view',
  // COURSE TEMPLATE
  ADMIN_COURSE_TEMPLATE_CREATE: 'create',
  ADMIN_COURSE_TEMPLATE_VIEW: 'view',
  // SCHEDULE
  ADMIN_SCHEDULE_CREATE: 'create',
  ADMIN_SCHEDULE_VIEW: 'view',
  ADMIN_SCHEDULE_EDIT: 'edit',
  SCHEDULE_VIEW: 'view',
  SCHEDULE_EDIT: 'edit',
  // LESSON HISTORY
  ADMIN_LESSON_HISTORY_VIEW: 'view',
  LESSON_HISTORY_VIEW: 'view',
  // PROFILE
  PROFILE_VIEW: 'view',
  PROFILE_EDIT: 'edit',
  PROFILE_DELETE: 'delete',
  PROFILE_QUALIFICATIONS: 'qualifications',
  PROFILE_SUBJECTS: 'subjects',
  PROFILE_PARENT: 'parentDetails',
  PROFILE_TUTOR: 'tutorDetails',
  PROFILE_BANK: 'bankDetails',
  PROFILE_CURRICULUM: 'curriculum',
  PROFILE_EXAM_DATE: 'examDate',
  PROFILE_NATIONALITY: 'nationality',
  PROFILE_EMERGENCY_NUMBER: 'emergencyNumber',
  // TRIAL CLASS
  TRIAL_CLASS_VIEW: 'view',
  TRIAL_CLASS_CREATE: 'create',
  ADMIN_TRIAL_CLASS_VIEW: 'view',
  ADMIN_TRIAL_CLASS_CREATE: 'create',
  ADMIN_TRIAL_CLASS_EDIT: 'edit',
  PROFILE_DATE_OF_BIRTH: 'dateOfBirth',
};

export const USER_PERMISSION: {
  // COURSE
  ADMIN_COURSE: TUserPermissionAdminCourses;
  COURSE: TUserPermissionCourses;
  // COURSE TEMPLATE
  ADMIN_COURSE_TEMPLATE: TUserPermissionAdminCourseTemplates;
  // SCHEDULE
  ADMIN_SCHEDULE: TUserPermissionAdminSchedule;
  SCHEDULE: TUserPermissionSchedule;
  // LESSON HISTORY
  ADMIN_LESSON_HISTORY: TUserPermissionAdminLessonHistory;
  LESSON_HISTORY: TUserPermissionLessonHistory;
} = {
  // COURSE
  ADMIN_COURSE: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_DELETE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_PUBLISH,
  ],
  COURSE: [USER_PERMISSION_INDIVIDUALS.COURSE_VIEW],
  // COURSE TEMPLATE
  ADMIN_COURSE_TEMPLATE: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_VIEW,
  ],
  // SCHEDULE
  ADMIN_SCHEDULE: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_EDIT,
  ],
  SCHEDULE: [
    USER_PERMISSION_INDIVIDUALS.SCHEDULE_VIEW,
    USER_PERMISSION_INDIVIDUALS.SCHEDULE_EDIT,
  ],
  // LESSON HISTORY
  ADMIN_LESSON_HISTORY: [USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_VIEW],
  LESSON_HISTORY: [USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_VIEW],
};

export interface IUserPermissions {
  quiz?: Array<'view'>;
  submission?: Array<'view' | 'create' | 'edit' | 'delete'>;
  quizHistory?: Array<'view'>;
  course?: TUserPermissionCourses;
  schedule?: TUserPermissionSchedule;
  profile?: TUserPermissionProfile;
  trialClass?: TUserPermissionTrialClass;
  lessonHistory?: TUserPermissionLessonHistory;
  // ADMIN
  adminSubTopic?: Array<'view' | 'create' | 'edit' | 'delete'>;
  adminQuestion?: Array<'view' | 'create' | 'edit' | 'delete' | 'approve'>;
  adminQuiz?: Array<'view' | 'create' | 'edit' | 'delete' | 'publish'>;
  adminStudent?: Array<'view'>;
  adminCourse?: TUserPermissionAdminCourses;
  adminCourseTemplate?: TUserPermissionAdminCourseTemplates;
  adminSchedule?: TUserPermissionAdminSchedule;
  adminLessonHistory?: TUserPermissionAdminLessonHistory;
  adminTrialClass?: TUserPermissionAdminTrialClass;
}

export const USER_ROLE_NAMES = {
  ADMIN: 'Admin',
  APPROVER: 'Approver',
  ENTERER: 'Enterer',
  STUDENT: 'Student',
  TUTOR: 'Tutor',
  GUEST: 'Guest',
};

export interface IUserCurriculum {
  curriculum: string;
  grade: string;
  subject: string;
  subjectCode: string;
}

export interface IUserExperience {
  _id?: string;
  jobTitle: string;
  workPlace: string;
  startDate: string;
  endDate: string;
}
export interface IUserQualification {
  _id?: string;
  qualification: string;
  institute: string;
  startDate: string;
  endDate: string;
}

export interface IUserAchievement {
  _id?: string;
  awardTitle: string;
  awardBody: string;
  awardDate: string;
}

export interface IUserLanguage {
  _id?: string;
  language: string;
  proficiency: string;
}

interface IUserProfile {
  experiences?: IUserExperience[];
  qualifications?: IUserQualification[];
  achievements?: IUserAchievement[];
  languages?: IUserLanguage[];
}

export interface IUserSubject {
  _id?: string;
  subject: string;
  curriculum: string;
  grade: string;
}

export interface IUserTutorDetails {
  teachingStatus?: string;
  availability?: string;
  isCameraOn?: boolean;
  trialNoticePeriod?: number;
  classNoticePeriod?: number;
  strengths?: string[];
  bio?: string;
}
export interface IUserBankDetails {
  bankCountry?: string;
  bankName?: string;
  bankCode?: string;
  branchCode?: string;
  bankBranch?: string;
  accountNo?: string;
  accountHolderName?: string;
}
export interface IUser extends IBaseUser {
  isQuiztopiyaPendingTrial?: boolean;
  assessmentEngineDate?: string;
  quiztopiyaTrialExpiration?: string;
  curriculums?: Array<IUserCurriculum>;
  isContinueSubmissionWithTimerEnabled?: boolean;
  isLeadGenSubmitted?: boolean;
  students?: Array<{
    username: string;
  }>;
  gender?: string;
  dateOfBirth?: string;
  tutopiyaUserId?: string;
  phoneNumber?: string;
  country?: string;
  curriculum?: string;
  grade?: string;
  examDate?: string;
  schoolName?: string;
  parentName?: string;
  parentEmail?: string;
  parentPhoneNumber?: string;
  nationality?: string;
  emergencyNumber?: string;
  notifications?: IUserNotifications;
  profile?: IUserProfile;
  subjects?: IUserSubject[];
  tutorDetails?: IUserTutorDetails;
  bankDetails?: IUserBankDetails;
}

export interface IUsersState {
  status: 'idle' | 'loading' | 'rejected';
  count: number;
  user: IUser | undefined;
  enrolmentCourseRelatedStudentList: IUser[];
  enrolmentCourseRelatedTutorList: IUser[];
  // Admin
  userListDataForAdminForDropDown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IBaseUser>;
  };
}

const initialState: IUsersState = {
  status: 'idle',
  count: 0,
  user: undefined,
  enrolmentCourseRelatedStudentList: [],
  enrolmentCourseRelatedTutorList: [],
  userListDataForAdminForDropDown: undefined,
};

export const UsersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    resetEnrolmentCourseRelatedStudentList: (state) => {
      state.enrolmentCourseRelatedStudentList = [];
    },
    resetEnrolmentCourseRelatedTutorList: (state) => {
      state.enrolmentCourseRelatedTutorList = [];
    },
    resetUserListDataForAdmin: (state) => {
      state.userListDataForAdminForDropDown = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET USER BY ID
      .addCase(adminGetUserById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetUserById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.user = action.payload;
      })
      .addCase(adminGetUserById.rejected, (state) => {
        state.status = 'rejected';
      })

      // GET ENROLMENT COURSE RELATED STUDENTS
      .addCase(getEnrolmentCourseRelatedStudents.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getEnrolmentCourseRelatedStudents.rejected, (state) => {
        state.status = 'rejected';
      })
      .addCase(getEnrolmentCourseRelatedStudents.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        state.enrolmentCourseRelatedStudentList = result;
      })

      // GET ENROLMENT COURSE RELATED TUTORS
      .addCase(getEnrolmentCourseRelatedTutors.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getEnrolmentCourseRelatedTutors.rejected, (state) => {
        state.status = 'rejected';
      })
      .addCase(getEnrolmentCourseRelatedTutors.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        state.enrolmentCourseRelatedTutorList = result;
      })

      // V2
      // Admin
      // Get user list
      .addCase(getUserListForDropDownAsAdmin.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getUserListForDropDownAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;
        const newUserListDataForAdminForDropDown = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.userListDataForAdminForDropDown = newUserListDataForAdminForDropDown;
      })
      .addCase(getUserListForDropDownAsAdmin.rejected, (state) => {
        state.status = 'rejected';
      })
      // DELETE USER
      .addCase(deleteUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.status = 'idle';
        removeAuthTokenToLocalStorage();
        removeAuthUserToLocalStorage();
        removeAdminPortalEditModeValueFromLocalStorage();
        window.location.href = `${process.env.PUBLIC_URL}/`;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.status = 'rejected';
      });
  },
});

export const getUsersState = (state: RootState): RootState['users'] =>
  state.users;

export const {
  resetUserListDataForAdmin,
  resetEnrolmentCourseRelatedStudentList,
  resetEnrolmentCourseRelatedTutorList,
} = UsersSlice.actions;

export * from './AdminUsersApi';
export default UsersSlice.reducer;
